import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { useFocusEffect, useNavigation, usePreventRemove } from 'expo-router/react-navigation';
import { useAppNavigationLeave } from '@/components/navigation/AppNavigationLeave';

type LeaveRequest = {
  scope: string | undefined;
  promise: Promise<void>;
  authorizeRemoval: boolean;
};

/** Header e remoção compartilham a confirmação local e uma única continuação. */
export function useChecklistLeaveGuard(input: {
  prevent: boolean;
  bypass: () => boolean;
  flush: () => Promise<void>;
  onError: (error: unknown) => void;
  getScope?: () => string | null;
}): { saving: boolean; leave: (continueLeaving: () => void) => Promise<void> } {
  const navigation = useNavigation();
  const appNavigationLeave = useAppNavigationLeave();
  const latest = useRef(input);
  latest.current = input;
  const active = useRef<LeaveRequest | null>(null);
  const mounted = useRef(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const invalidate = useCallback(() => {
    active.current = null;
    if (mounted.current) setSaving(false);
  }, []);
  useFocusEffect(useCallback(() => () => invalidate(), [invalidate]));
  useEffect(() => {
    if (active.current && latest.current.getScope?.() !== active.current.scope) invalidate();
  });

  const leave = useCallback((continueLeaving: () => void): Promise<void> => {
    const scope = latest.current.getScope?.();
    if (scope === null || !mounted.current) return Promise.resolve();
    if (active.current && active.current.scope === scope) return active.current.promise;
    const request: LeaveRequest = { scope, promise: Promise.resolve(), authorizeRemoval: false };
    active.current = request;
    const isCurrent = () => mounted.current && active.current === request && latest.current.getScope?.() === scope;
    setSaving(true);
    request.promise = Promise.resolve().then(async () => {
      let issued = false;
      try {
        if (!isCurrent()) return;
        if (!latest.current.bypass()) await latest.current.flush();
        if (!isCurrent()) return;
        request.authorizeRemoval = true;
        continueLeaving();
        issued = true;
      } catch (error) {
        if (isCurrent()) latest.current.onError(error);
      } finally {
        // A fila do Router pode consumir a ação depois deste await: manter trava até blur.
        if (!issued && active.current === request) {
          active.current = null;
          if (mounted.current) setSaving(false);
        }
      }
    });
    return request.promise;
  }, [invalidate]);

  useFocusEffect(useCallback(() => appNavigationLeave?.register(leave), [appNavigationLeave, leave]));

  usePreventRemove(input.prevent, ({ data }) => {
    const scope = latest.current.getScope?.();
    if (scope === null || !mounted.current) return;
    const request = active.current;
    if (request && request.scope !== scope) return;
    if (latest.current.bypass()) {
      invalidate();
      navigation.dispatch(data.action);
      return;
    }
    if (request) {
      if (!request.authorizeRemoval) return;
      request.authorizeRemoval = false;
      navigation.dispatch(data.action);
      return;
    }
    void leave(() => {
      // Esta ação já foi interceptada; sua repetição conserva a autorização do navegador.
      if (active.current) active.current.authorizeRemoval = false;
      navigation.dispatch(data.action);
    });
  });
  useEffect(() => {
    if (Platform.OS !== 'web' || !input.prevent) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [input.prevent]);
  return { saving, leave };
}
